import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCLP, dateLabel, statusBadge, formatTracking } from "../../lib/format";
import { Plus, CircleNotch, CaretLeft, CaretRight, Eye, CheckCircle, Star } from "@phosphor-icons/react";

const PAGE_SIZE = 10;
const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "active", label: "Activos" },
  { key: "done", label: "Historial" },
];

export default function ClientDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [freights, setFreights] = useState(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const load = () => api.get("/freights/mine").then((r) => setFreights(r.data)).catch(() => setFreights([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [filter]);

  const confirmReception = async (svcId) => {
    if (!window.confirm("¿Confirmas que recibiste la carga? Esto marcará el servicio como completado.")) return;
    try { await api.post(`/services/${svcId}/status`, { status: "COMPLETADO" }); toast.success("Recepción confirmada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (freights === null) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const shown = freights.filter((f) => {
    if (filter === "all") return true;
    if (filter === "done") return ["COMPLETADO", "CANCELADO"].includes(f.status);
    return !["COMPLETADO", "CANCELADO"].includes(f.status);
  });
  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const paged = shown.slice(start, start + PAGE_SIZE);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="client-dashboard">
      <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Panel del cliente</span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Hola, {user.name}</h1>
        </div>
        <Link to="/app/client/publish" data-testid="publish-freight-btn"
          className="px-6 py-3 rounded-full bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95">
          <Plus size={20} weight="bold" /> Publicar un flete
        </Link>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap" data-testid="client-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${filter === f.key ? "bg-[#0F1115] text-white" : "border border-zinc-300 text-zinc-600 hover:border-[#0F1115]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="client-empty">
          No tienes fletes en esta vista. ¡Publica tu primer flete!
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 bg-zinc-50 border-b border-zinc-200">
                  <th className="px-4 py-2.5 font-bold">N° Traslado</th>
                  <th className="px-4 py-2.5 font-bold">Ruta</th>
                  <th className="px-4 py-2.5 font-bold">Categoría</th>
                  <th className="px-4 py-2.5 font-bold">Fecha</th>
                  <th className="px-4 py-2.5 font-bold">Estado</th>
                  <th className="px-4 py-2.5 font-bold text-right">Precio</th>
                  <th className="px-4 py-2.5 font-bold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((f) => {
                  const badge = statusBadge(f.status);
                  const svc = f.service;
                  const canConfirm = svc && ["EN_TRASLADO", "ENTREGADO"].includes(svc.status);
                  const canRate = svc && svc.status === "COMPLETADO" && !svc.rated;
                  const target = svc ? `/app/service/${svc.id}` : `/app/freight/${f.id}`;
                  return (
                    <tr key={f.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`freight-row-${f.id}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-600 whitespace-nowrap">{svc ? formatTracking(svc.trackingNumber) : "—"}</td>
                      <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{f.origin.city} → {f.destination.city}</td>
                      <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">{f.category}</td>
                      <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">{dateLabel(f.requestedDate || f.createdAt)}</td>
                      <td className="px-4 py-2.5"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.color}`}>{badge.label}</span></td>
                      <td className="px-4 py-2.5 text-right font-bold whitespace-nowrap">{formatCLP(svc?.agreedPrice || f.proposedPrice)}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => nav(target)} data-testid={`view-freight-${f.id}`} title="Ver"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-xs font-semibold text-zinc-600 hover:border-[#0F1115] active:scale-95"><Eye size={14} /> Ver</button>
                          {canConfirm && (
                            <button onClick={() => confirmReception(svc.id)} data-testid={`confirm-reception-${f.id}`} title="Confirmar recepción"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 active:scale-95"><CheckCircle size={14} weight="fill" /> Recibí</button>
                          )}
                          {canRate && (
                            <button onClick={() => nav(target)} data-testid={`rate-freight-${f.id}`} title="Valorizar"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF5A36] text-white text-xs font-bold hover:bg-[#E04B2A] active:scale-95"><Star size={14} weight="fill" /> Valorizar</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-3" data-testid="client-pagination">
            <span className="text-sm text-zinc-500">Mostrando {start + 1}–{Math.min(start + PAGE_SIZE, shown.length)} de {shown.length}</span>
            <div className="flex items-center gap-2">
              <button data-testid="client-prev" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 active:scale-95"><CaretLeft size={14} weight="bold" /> Anterior</button>
              <span className="text-sm font-semibold text-zinc-700">Página {currentPage} de {totalPages}</span>
              <button data-testid="client-next" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 active:scale-95">Siguiente <CaretRight size={14} weight="bold" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
